import type { AxiosRequestConfig } from 'axios'
import { type QueryFunctionContext, type UndefinedInitialDataOptions, useQuery, UseQueryResult } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMemoizedFn } from 'ahooks'
import { QueryClientInstance } from './client/instance'
import { useIsMobile } from '@/hooks/use-mobile'

/** 取消請求 Cache */
export const autoCancelMap: Record<string, AbortController> = {}

/** 取消請求 */
export const cancelRequest = (key: string) => {
  if (!(key in autoCancelMap)) return
  const controller = autoCancelMap[key]
  if (typeof window !== 'undefined') requestAnimationFrame(() => controller?.abort?.())
  delete autoCancelMap[key]
}

/** 模擬 useSWR 縮寫 */
export const useSWR = <TResult = any, TParam = any>(
  key: string | readonly unknown[] | null,
  // API Method
  fetcher: (p: TParam) => Promise<TResult>,
  // useQuery Options
  params?: Partial<UndefinedInitialDataOptions> & {
    // API Params
    params?: TParam | ((query: QueryFunctionContext) => Promise<TParam> | TParam | null)
    // Axios Config
    config?: AxiosRequestConfig
  },
) => {
  // 將 key 轉換為 string（用於內部比較，需用 JSON.stringify 處理對象）
  const keyCache = typeof key === 'string' ? key : key ? JSON.stringify(key) : null

  // 將 key 轉換為數組格式（用於 queryKey）
  const keyArray = typeof key === 'string' ? [key] : key

  /** Fetcher */
  const queryFn = useMemoizedFn(async (query: QueryFunctionContext) => {
    const { signal } = query
    let apiParams: TParam | undefined | null
    if (typeof params?.params === 'function') apiParams = await Promise.all([(params.params as (query: QueryFunctionContext) => Promise<TParam> | TParam | null)(query)]).then(([p]) => p)
    else apiParams = params?.params
    if (apiParams === null) return null
    return fetcher.call({ signal, ...params?.config }, apiParams as TParam)
  })

  /** Query Options */
  const options = useMemo(() => {
    const options: Record<string, any> = {
      queryKey: keyArray,
      queryFn,
      ...{ ...params, params: undefined, config: undefined },
    }
    options.enabled = params && 'enabled' in params ? !!params?.enabled : key !== null
    return options
  }, [keyCache, queryFn, params?.enabled])

  return useQuery<TResult>(options as UndefinedInitialDataOptions<TResult>)
}

/**
 * Page 分頁或無限加載
 * 1. 只能用於 API 返回的格式中包含 rows 的函式，且參數有 page pageSize
 * 2. 預設 pageSize = 20，可帶入 params 覆蓋
 * 3. 純無限的可以用 useInfiniteQuery 代替，不需要用這隻
 */
export const usePage = <TResult = any, TParam = any>(
  key: string | readonly unknown[] | null,
  // API Method
  fetcher: (p: TParam) => Promise<TResult>,
  // useQuery Options
  options?: Partial<UndefinedInitialDataOptions> & {
    // API Params
    params?: TParam | ((query: QueryFunctionContext) => Promise<TParam> | TParam | null)
    // Axios Config
    config?: AxiosRequestConfig
    // 模式，無限加載或分頁，默認根據裝置自動判斷（Mobile: infinite, PC: page）
    mode?: 'page' | 'infinite'
  },
) => {
  const isMobile = useIsMobile()

  // 參數
  const { mode = isMobile ? 'infinite' : 'page', params, config, ...queryOptions } = options || {}

  // 將 key 轉換為 string（用於內部比較，需用 JSON.stringify 處理對象）
  const keyCache = typeof key === 'string' ? key : key ? JSON.stringify(key) : null

  // 將 key 轉換為數組格式（用於 queryKey）
  const keyArray = typeof key === 'string' ? [key] : key

  // 頁碼
  const pageMap = useRef<Map<number, RowsItem<TResult>[]>>(new Map())
  const totalPage = useRef(-1)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  /** Fetcher */
  const queryFn = useMemoizedFn(async (query: QueryFunctionContext) => {
    let apiParams: TParam | undefined | null
    if (typeof params === 'function') apiParams = await Promise.all([(params as (query: QueryFunctionContext) => Promise<TParam> | TParam | null)(query)]).then(([p]) => p)
    else apiParams = params
    if (apiParams === null) return null
    const finalParams = { pageSize: 10, page, ...apiParams } as TParam
    return fetcher.call({ signal: query.signal, ...config }, finalParams).then((res) => ({ ...res, params: finalParams, random: Math.random() }))
  })

  /** API */
  const [queryKey, setQueryKey] = useState('')
  const enabled = key === null || !queryKey ? false : queryOptions?.enabled
  const query = useQuery({
    queryKey: keyArray ? [...keyArray, page] : [queryKey, page],
    queryFn,
    staleTime: 60 * 1000,
    ...queryOptions,
    enabled,
  })

  useEffect(() => {
    if (query?.isRefetching || query?.isLoading) return
    if (!query.data) return
    /** update page */
    const { page: paramsPage = 1, pageSize = 0 } = (query?.data as any)?.params || {}
    const { rows = [], total = 0 } = query.data || ({} as any)
    setTotalCount(total)
    pageMap.current.set(paramsPage, rows || [])
    totalPage.current = Math.max(Math.ceil(total / pageSize), 0)
    /** update list */
    if (mode === 'page') {
      setList(pageMap.current.get(paramsPage) || [])
    } else {
      setList(Array.from(pageMap.current.values()).flat())
    }
  }, [query?.data, query?.isRefetching, query?.isLoading])

  /** 清除數據 */
  const onClear = () => {
    pageMap.current.clear()
    totalPage.current = -1
    setTotalCount(0)
    setPage(1)
    setList([])
    if (keyArray) QueryClientInstance.removeQueries({ queryKey: keyArray })
  }

  /** 重新加載 */
  const refreshRef = useRef(false)
  const refetch = async () => {
    if (refreshRef.current) return
    try {
      onClear()
      refreshRef.current = true
      await Promise.resolve(requestAnimationFrame)
      await query.refetch()
    } finally {
      refreshRef.current = false
    }
  }

  /** 列表 */
  const [list, setList] = useState<RowsItem<TResult>[]>([])

  /** 結束 */
  const isFinish = totalPage.current > -1 && totalPage.current <= pageMap.current.size

  /** 加載指定頁或下一頁 */
  const nextPage = useMemoizedFn((nextPage?: number) => {
    // 無限
    if (mode === 'infinite') {
      if (query.isLoading || query.isFetching || query.isRefetching) return // 忙碌中
      if (isFinish) return // 已結束
      setPage((prev) => prev + 1)
    }
    // 頁碼
    else if (mode === 'page') {
      if (!nextPage || nextPage > totalPage.current) return // 未選擇分頁
      setPage(nextPage)
      // cache
      const list = pageMap.current.get(nextPage)
      if (list) setList(list)
      // clear
      else setList([])
    }
  })

  /**
   * 清除數據
   */
  const hotReload = useRef<string | null>('')
  useEffect(() => {
    if (hotReload.current === keyCache) return
    hotReload.current = keyCache
    if (!keyCache) return
    onClear()
    setQueryKey(keyCache)
  }, [keyCache])

  return {
    // useQuery
    query: query as UseQueryResult<TResult>,
    refetch,
    nextPage,
    // 清除數據
    onClear,
    // 讀取中
    isLoading: query.isLoading || query.isFetching || query.isRefetching,
    // 依據頁碼保存的資料
    pageData: pageMap.current,
    // 分頁返回的是當前頁碼的列表, 無限模式返回的是完整列表
    list,
    // 頁碼
    page,
    totalPage: totalPage.current,
    setPage,
    // 總數
    totalCount, // API 有返回才會有
    // 已結束, 只有無限模式能用
    isFinish,
    // 是否啟用
    enabled,
  }
}

/** 懶人拿錯誤用 */
export const getResponseError = <Any = any>(e: Any, _default: string) => (e as any)?.response?.data?.msg || (e as any)?.message || _default
